import * as _ from 'lodash';

import { DependencyInfoOptions } from '../../../../../../../../shared/reports';
import { selectOptionFactory } from '../../../../../../../utils';
import { ReportOptionDetails } from '../../../utils';
import { licenseIncludeOptions } from '../../constants';
import type { PackageLabel } from '../../../../../../../../shared/model-typings/packagelabel';
import type { PartialReportConfig } from '../../../../../../../../shared/reports/types';
import type { SelectOption } from '../../../../../../../types';
import type { IncludeFilterOption } from '../../types';

type SectionKey = keyof NonNullable<PartialReportConfig['sections']>;
type ToggleKey = keyof NonNullable<PartialReportConfig['toggles']>;
type DependencyDataKey = keyof NonNullable<PartialReportConfig['dependencyData']>;

const sectionToIncludeOption: Record<SectionKey, IncludeFilterOption> = {
  dependencySummary: 'includeDependencySummary',
  firstPartyNoticeFiles: 'includeNoticeFiles',
  directDependencies: 'includeDirectDependencies',
  deepDependencies: 'includeDeepDependencies',
  snippetDependencies: 'includeSnippetDependencies',
  projectDeclaredLicenses: 'includeProjectLicense',
  firstPartyLicenses: 'includeLicenseScan',
  licenseList: 'includeLicenseList',
  copyrightList: 'includeCopyrightList',
  fossaDependencies: 'includeFOSSADependencies',
  openVulnerabilities: 'includeOpenVulnerabilities',
  closedVulnerabilities: 'includeClosedVulnerabilities',
  supportStatus: 'includeSupportStatus',
  licenseFileMatches: 'includeFileMatches',
  concludedLicenses: 'includeConcludedLicenses',
  nonLicenseCopyrights: 'includeNonLicenseCopyrights',
};

// The as is necessary to cast the result of Object.entries to the correct type, otherwise it treats all keys as strings.
const sectionToIncludeOptionEntries: Array<[SectionKey, IncludeFilterOption]> = Object.entries(
  sectionToIncludeOption,
) as Array<[SectionKey, IncludeFilterOption]>;

const toggleToIncludeOption: Record<ToggleKey, IncludeFilterOption> = {
  useHashAndVersionData: 'includeHashAndVersionData',
};

// The as is necessary to cast the result of Object.entries to the correct type, otherwise it treats all keys as strings.
const toggleToIncludeOptionEntries: Array<[ToggleKey, IncludeFilterOption]> = Object.entries(
  toggleToIncludeOption,
) as Array<[ToggleKey, IncludeFilterOption]>;

const dependencyDataToInfoOption: Partial<Record<DependencyDataKey, keyof typeof DependencyInfoOptions>> = {
  projects: 'Project',
  name: 'Library',
  authors: 'Authors',
  description: 'Description',
  homepage: 'ProjectUrl',
  packageManager: 'Source',
  downloadUrl: 'PackageDownloadUrl',
  concludedLicenses: 'ConcludedLicense',
  declaredLicenses: 'License',
  discoveredLicenses: 'OtherLicenses',
  copyrights: 'Copyrights',
  licenseUrl: 'LicenseFileURL',
  issueResolutionNotes: 'IssueNotes',
  dependencyPaths: 'DependencyPaths',
  filePaths: 'FilePath',
  noticeFiles: 'NoticeFiles',
  fullLicenseText: 'FullTextLicense',
  licenseHeaders: 'CustomTextLicense',
};

const dependencyDataToInfoOptionEntries: Array<[DependencyDataKey, keyof typeof DependencyInfoOptions]> =
  // The as is necessary to cast the result of Object.entries to the correct type, otherwise it treats all keys as strings.
  Object.entries(dependencyDataToInfoOption) as Array<[DependencyDataKey, keyof typeof DependencyInfoOptions]>;

const dependencyDataToIncludeOption: Partial<Record<DependencyDataKey, IncludeFilterOption>> = {
  licenseHeaders: 'includeLicenseHeaders',
  packageLabels: 'includePackageLabels',
};

const dependencyDataToIncludeOptionEntries: Array<[DependencyDataKey, IncludeFilterOption]> =
  Object.entries(dependencyDataToIncludeOption) as Array<[DependencyDataKey, IncludeFilterOption]>;

function makeIncludeOption(key: IncludeFilterOption): SelectOption<IncludeFilterOption> {
  return selectOptionFactory(key, ReportOptionDetails[key]?.title ?? '');
}

type FilterState = {
  flatIncludeValues: Array<SelectOption<IncludeFilterOption>>;
  licenseIncludeValues: Array<SelectOption<IncludeFilterOption>>;
  dependencyMetadataValues: SelectOption[];
  packageLabelExclusionValues: SelectOption[];
};

/**
 * Maps a saved PartialReportConfig to the option state used by the Options component.
 * Package label exclusions are stored as IDs in the config but the filter expects names,
 * so availablePackageLabels is used to hydrate the IDs into SelectOptions with names.
 */
export function mapReportConfigToOptionState(
  config: PartialReportConfig,
  availablePackageLabels: PackageLabel[],
): FilterState {
  const flatIncludeValues: Array<SelectOption<IncludeFilterOption>> = [];
  const licenseIncludeValues: Array<SelectOption<IncludeFilterOption>> = [];
  const dependencyMetadataValues: SelectOption[] = [];

  // Map sections
  sectionToIncludeOptionEntries.forEach(([sectionKey, includeKey]) => {
    if (config.sections?.[sectionKey]) {
      const option = makeIncludeOption(includeKey);
      if (licenseIncludeOptions.has(includeKey)) {
        licenseIncludeValues.push(option);
      } else {
        flatIncludeValues.push(option);
      }
    }
  });

  // Map toggles
  toggleToIncludeOptionEntries.forEach(([toggleKey, includeKey]) => {
    if (config.toggles?.[toggleKey as ToggleKey]) {
      const option = makeIncludeOption(includeKey);
      if (licenseIncludeOptions.has(includeKey)) {
        licenseIncludeValues.push(option);
      } else {
        flatIncludeValues.push(option);
      }
    }
  });

  // Map dependency data to metadata options
  dependencyDataToInfoOptionEntries.forEach(([dataKey, infoKey]) => {
    if (infoKey && config.dependencyData?.[dataKey]) {
      dependencyMetadataValues.push(selectOptionFactory(infoKey, DependencyInfoOptions[infoKey]));
    }
  });

  // Map dependency data keys that correspond to include options
  dependencyDataToIncludeOptionEntries.forEach(([dataKey, includeKey]) => {
    if (config.dependencyData?.[dataKey]) {
      const option = makeIncludeOption(includeKey);
      if (licenseIncludeOptions.has(includeKey)) {
        licenseIncludeValues.push(option);
      } else {
        flatIncludeValues.push(option);
      }
    }
  });

  // Hydrate package label exclusion IDs into SelectOptions with names
  const labelById = new Map(availablePackageLabels.map((l) => [l.id, l.name]));
  const packageLabelExclusionValues: SelectOption[] = (config.excludeFields?.packageLabels ?? [])
    .map((id) => labelById.get(id))
    .filter((name) => _.isString(name))
    .map((name) => selectOptionFactory(name));

  return { flatIncludeValues, licenseIncludeValues, dependencyMetadataValues, packageLabelExclusionValues };
}
